import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { taskFormsApi, webFormsApi } from '../axios';
import { resumeApi } from '../axios/resume';
import { TaskForm } from '../types';

export function useAllFormsQuery(includeDeleted?: boolean) {
  return useQuery({
    queryKey: ['forms', `include-deleted-${includeDeleted}`],
    queryFn: () => taskFormsApi.getAllTaskForms(undefined, undefined, includeDeleted),
  });
}

export function useFormsForTaskAssignFilterQuery() {
  return useQuery({
    queryKey: ['forms-for-task-assign-filter'],
    queryFn: () => taskFormsApi.getFormsForTaskAssignFilter(),
  });
}

export function useFormQuestionsQuery(formId: TaskForm['id'], token?: string) {
  return useQuery({
    queryKey: ['questions', formId],
    queryFn: async () => {
      const form = await webFormsApi.getForm(formId.toString(), token);
      return form;
    },
    select: (data) => data.questions,
    enabled: !!formId,
  });
}

/**
 * Forms for the AskDataScope "@" panel: the PAGINATED endpoint, which is the one
 * the spec named for this category.
 *
 * It replaced `useFormsAllForMentionQuery` (`/task_forms_all`). That one returns
 * every form in the account in one response, so it could not be fired just because
 * the panel opened, and gating it behind "something was typed" left Formularios as
 * the only category with no rows and no count on `@` — a direct AC-1 violation, and
 * one that reads as "this account has no forms" rather than as "type to search".
 *
 * A page of 20 plus a real total satisfies AC-1 and AC-10 at once, and costs a page
 * instead of the table.
 */
export function useFormsPageForMentionQuery(searchQuery: string) {
  return useQuery({
    queryKey: ['forms-mention-page', searchQuery],
    queryFn: () => resumeApi.getTaskFormsForMention(1, searchQuery || undefined),
  });
}

// Kept for callers outside the mention panel. NOT for the panel: see above.
export function useFormsAllForMentionQuery(
  searchQuery: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['forms-all-mention', searchQuery],
    queryFn: () => taskFormsApi.getAllTaskForms(searchQuery || undefined),
    enabled,
  });
}

export function useFormsInfiniteQuery(searchQuery: string) {
  return useInfiniteQuery({
    queryKey: ['forms', searchQuery],
    queryFn: ({ pageParam = 1 }) => resumeApi.getTaskFormsWithSearch(pageParam, searchQuery),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page > 1) {
        return firstPage.page - 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });
}

export function useFormQuery(formId: TaskForm['id']) {
  return useQuery({
    queryKey: ['form', formId],
    queryFn: () => resumeApi.getTaskForm(formId),
    select: (data) => data.form,
    enabled: !!formId,
  });
}

export function useTaskFormFoldersQuery() {
  return useQuery({
    queryKey: ['taskFormFolders'],
    queryFn: () => taskFormsApi.getTaskFormsFolders(),
  });
}
